import {StateProvider, IHookRegistry, UIRouter} from "@uirouter/angularjs";
import {L} from "../lt-ng/landcare-toolkit";
import filterNodes = L.tree.filterNodes;
import Item = L.tree.Item;
import {GeoAppEvents} from "../controllers/events/geoapp_events";
import findNode = L.tree.findNode;
import {ILayerTreeManipulationService} from "../services/oe/layer-tree-manipulation";
import getVisibleScienceLayers = L.tree.getVisibleScienceLayers;
import {Panes} from "../controllers/panes";
import {TourEvents} from "../services/ITourService";
import {IMapShareService} from "../services/MapShareAPI";
import {joinPaths} from "../utility/joinPaths";

export enum StateNames {
    base='base',
    group='group',
    layers='layers',
    layersWithClasses='layersWithClasses',
}

export interface IStateLayerParams {
    group: string;
    layers: string;
    classes?: string;
    contextLayers?: string;
    m?: string;
}

export interface ICustomTransitionOptions {
    doNotCloseLayerSelector?: boolean;
}

export function configureLayersRouter ($stateProvider: StateProvider) {
    if (!document.querySelector('html.page-map')) return;

    const config: any = (<any> window).LTConfiguration;
    const urlBase = config.appPageLink || window.location.pathname;

    const optionalParams = '?m&savedReport&tour&soilmapuc&gislayer&pinCoordinate&contextLayers'

    const baseState = {
        name: StateNames.base,
        url: `${urlBase}${optionalParams}`
    };

    const groupState = {
        name: StateNames.group,
        url: joinPaths([urlBase, ':group']) + optionalParams
    };

    const layerState = {
        name: StateNames.layers,
        url: joinPaths([urlBase, ':group', ':layers']) + optionalParams
    };

    const classesState = {
        name: StateNames.layersWithClasses,
        url: joinPaths([urlBase, ':group', ':layers', ':classes']) + optionalParams
    };

    $stateProvider.state(baseState);
    $stateProvider.state(groupState);
    $stateProvider.state(layerState);
    $stateProvider.state(classesState);
}

export function configureLayerRouteTransitions(
            $transitions: IHookRegistry,
            layerTreeManipulationService: ILayerTreeManipulationService,
            $rootScope: ng.IRootScopeService,
            $uiRouter: UIRouter,
            $q: ng.IQService,
            mapShareService: IMapShareService) {
    function handleContextLayers(contextLayerString: string, data: Item[]) {
        contextLayerString
            .split(",")
            .map(layerName => findNode(data, n => n.layerService && n.layerService.layerName == layerName))
            .filter(n => !!n)
            .forEach(node => {
                layerTreeManipulationService.toggleLayer(node, true, { silent: true });
            });
    }

    function handleMapState(params: IStateLayerParams): ng.IPromise<boolean> {
        const payload = window.sessionStorage.getItem('mapState');

        if (!payload) return;

        try {
            if (params.m || sessionStorage.getItem('clearState') === 'true') {
                return $q.resolve(false);
            }

            const state = JSON.parse(payload);

            const skipLayers = !!(params.contextLayers || params.layers);

            return mapShareService.zoomTo(state, skipLayers).then(() => {
                const layers = L.tree.getVisibleScienceLayers(this.mapService.treeData) || [];
                const layerToActivate = layers[0];
                if (layerToActivate && !(layerToActivate.checked && layerToActivate.extraState.metaLayerSelector.checked)) {
                    this.changeStateForActiveLayers(layerToActivate, layerToActivate, { doNotCloseLayerSelector: true });
                }
                return true;
            }).catch(() => false);
        } catch (error) {
            console.warn(error);
        } finally {
            window.sessionStorage.removeItem('mapState');
        }

        return $q.resolve(false);
    }

    $transitions.onEnter({ to: StateNames.base }, (transition, state) => {
        console.log('Transition:OnEnter: base')
        document.title = window['LTConfiguration'].appPageTitle || document.title;

        const params = transition.params();
        if (params['contextLayers']) {
            layerTreeManipulationService.treeDataPromise.then(data => {
                handleContextLayers((params as any).contextLayers, data);
            })
        } else {
            handleMapState(params as IStateLayerParams);
        }
    });

    $transitions.onEnter({ to: StateNames.group }, (transition, state) => {
        console.log('Transition:OnEnter: group')
        const params = transition.params();
        const groupName = params.group;

        document.title = `${groupName} » ${window['LTConfiguration'].appPageTitle || document.title}`

        const from = transition.$from();
        if (from.name) {
            // return if this isn't the initial transition on page load
            return;
        }

        return <any> layerTreeManipulationService.treeDataPromise.then(treeData => {
            const group = findNode(treeData, (node: Item) => {
                return node.name === groupName;
            });

            if (!group) {
                transition.redirect($uiRouter.stateService.target('base'));
            }

            const visibleScienceLayer = getVisibleScienceLayers(treeData);

            if (visibleScienceLayer.length) {
                visibleScienceLayer.forEach(l => {
                    layerTreeManipulationService.toggleLayer(l, false, { silent: true });
                })
            }

            layerTreeManipulationService.expandNode(group, true);
            layerTreeManipulationService.includeLayers(group, ...group.children);

            if (!params.tour) {
                $rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, false, false);
            }

            console.log('$state', state);
            if (params['contextLayers']) {
                handleContextLayers((params as any).contextLayers, treeData);
            }

            $rootScope.$broadcast(GeoAppEvents.treeChange, false);
        });
    })

    $transitions.onEnter({ to: StateNames.layers }, (transition, state) => {
        console.log('Transition:OnEnter: layers')
        console.log(transition, state);
        const params = transition.params();
        const groupName = params.group;
        const layerNames = params.layers.split(',');

        const $from = transition.$from();

        return <any> layerTreeManipulationService.treeDataPromise.then(treeData => {
            const group = findNode(treeData, (node: Item) => {
                return node.name === groupName;
            });

            if (!group) {
                transition.redirect($uiRouter.stateService.target('base'));
            }

            const layers = filterNodes(treeData, (node: Item) => {
                const ls = node && node.layerService;
                return ls && layerNames.includes(ls.layerName) && !node.isCustom;
            });

            if (!layers.length) {
                transition.redirect($uiRouter.stateService.target('group', {group: groupName}));
            }

            const title = layers.map(l => l.name).join(', ');
            document.title = `${title} » ${window['LTConfiguration'].appPageTitle}`;

            layerTreeManipulationService.expandNode(group, true);
            layerTreeManipulationService.includeLayers(group, ...layers);

            const checkedLayers = layers.filter(l => layerNames.includes(l.layerService.layerName))

            checkedLayers.forEach((l, i) => {
                layerTreeManipulationService.toggleLayer(l, true, { showLegend: i === 0, silent: false });
            });

            const extraOptions: ICustomTransitionOptions = transition.options().custom || {}

            if (!extraOptions.doNotCloseLayerSelector && !params.tour)$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, false, false);

            $rootScope.$broadcast(GeoAppEvents.treeChange, false);

            console.log('$state', state);
            if (params['contextLayers']) {
                handleContextLayers((params as any).contextLayers, treeData);
            }

            if (!$from.name) {
                $rootScope.$broadcast(Panes.Events.SHOW_PANE, Panes.Ids.METADATA);
            }
        });
    });

    $transitions.onEnter({ to: StateNames.layersWithClasses }, (transition, state) => {
        console.log('Transition:OnEnter: layersWithClasses')
        const params = transition.params();
        const groupName = params.group;
        const layerNames = params.layers.split(',');
        const classes = params.classes.split(',').map(n => parseInt(n));

        const $from = transition.$from();

        return <any> layerTreeManipulationService.treeDataPromise.then(treeData => {
            const group = findNode(treeData, (node: Item) => {
                return node.name === groupName;
            });

            if (!group) {
                transition.redirect($uiRouter.stateService.target('base'));
                return;
            }

            const layers = filterNodes(treeData, (node: Item) => {
                const ls = node && node.layerService;
                return ls && layerNames.includes(ls.layerName) && !node.isCustom;
            });

            if (!layers.length) {
                transition.redirect($uiRouter.stateService.target('group', {group: groupName}));
            }

            const title = layers.map(l => l.name).join(', ');
            document.title = `${title} » ${window['LTConfiguration'].appPageTitle}`;

            layerTreeManipulationService.expandNode(group, true);
            layerTreeManipulationService.includeLayers(group, ...layers);

            const checkedLayers = layers.filter(l => layerNames.includes(l.layerService.layerName));

            const groupTypes = ["SCI_GROUP", "SCI_GROUP_CLASS_UNCHECKABLE"];

            let legendHasNotBeenShown = true;
            checkedLayers.forEach((l, i) => {
                if (groupTypes.includes(l.layerType)) {
                    const includedClasses = (l.children || []).filter(node => classes.includes(node.classId));
                    layerTreeManipulationService.expandNode(l, true);
                    includedClasses.forEach(classNode => {
                        layerTreeManipulationService.toggleLayer(classNode, true, { showLegend: legendHasNotBeenShown, silent: i < checkedLayers.length - 1 });
                    });
                } else {
                    layerTreeManipulationService.toggleLayer(l, true, { showLegend: legendHasNotBeenShown, silent: false });
                }
                legendHasNotBeenShown = false;
            });

            const extraOptions: ICustomTransitionOptions = transition.options().custom || {}
            if (!extraOptions.doNotCloseLayerSelector && !params.tour) $rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, false, false);

            console.log('$state', state);
            if (params['contextLayers']) {
                handleContextLayers((params as any).contextLayers, treeData);
            }

            $rootScope.$broadcast(GeoAppEvents.treeChange, false);

            if (!$from.name) {
                $rootScope.$broadcast(Panes.Events.SHOW_PANE, Panes.Ids.METADATA);
            }
        });
    });

    $transitions.onFinish({}, (transition) => {
        const params = transition.params();
        const startTour = !!params.tour;

        if (startTour) {
            if(!window.localStorage.getItem('termsShown')) {
                $rootScope.$on('termsAccepted', () => $rootScope.$emit(TourEvents.start));
            } else {
                $rootScope.$emit(TourEvents.start);
            }
        }
    });
}